/**
 * Branded enquiry email template — built with inline styles + tables
 * because email clients (especially Outlook) don't support external
 * stylesheets, flexbox/grid, or CSS variables. Colors below are taken
 * directly from the site's global.css design tokens.
 */

const COLORS = {
  navy: '#0A1045',
  navyDeep: '#060A2E',
  orange: '#FF4000',
  goldLight: '#FAD881',
  gold: '#EEC973',
  goldDeep: '#D9A94E',
  ivory: '#FBF9F4',
  white: '#FFFFFF',
  gray100: '#F2F3F7',
  gray300: '#D5D8E4',
  gray500: '#8A8FA8',
  gray700: '#4A4F6B',
};

function escapeHtml(str = '') {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function row(label, value) {
  if (!value) return '';
  return `
    <tr>
      <td style="padding:10px 0; border-bottom:1px solid ${COLORS.gray300};">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
          <tr>
            <td style="font-family:Arial,Helvetica,sans-serif; font-size:11px; font-weight:700; letter-spacing:1.5px; text-transform:uppercase; color:${COLORS.orange}; width:130px; vertical-align:top; padding-right:16px;">
              ${escapeHtml(label)}
            </td>
            <td style="font-family:Arial,Helvetica,sans-serif; font-size:15px; color:${COLORS.navy}; vertical-align:top;">
              ${escapeHtml(value)}
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

/**
 * Builds the full HTML email for a new website enquiry.
 * @param {object} form - { name, email, phone, company, eventType, message }
 */
function buildEnquiryEmailHtml(form) {
  const { name, email, phone, company, eventType, message } = form;

  return `
<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>New Enquiry — Collective KatalyX</title>
</head>
<body style="margin:0; padding:0; background-color:${COLORS.gray100}; font-family:Arial,Helvetica,sans-serif;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:${COLORS.gray100}; padding:32px 16px;">
    <tr>
      <td align="center">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px; width:100%; background-color:${COLORS.white}; border-radius:16px; overflow:hidden; box-shadow:0 8px 24px rgba(10,16,69,0.10);">

          <!-- Header -->
          <tr>
            <td style="background-color:${COLORS.navy}; background-image:linear-gradient(160deg, ${COLORS.navy} 0%, ${COLORS.navyDeep} 100%); padding:36px 40px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="font-family:Arial,Helvetica,sans-serif; font-size:20px; font-weight:800; letter-spacing:0.5px; color:${COLORS.white};">
                    COLLECTIVE <span style="color:${COLORS.gold};">KATALY<span style="color:${COLORS.orange};">X</span></span>
                  </td>
                </tr>
                <tr>
                  <td style="padding-top:6px; font-family:Arial,Helvetica,sans-serif; font-size:12px; letter-spacing:2px; text-transform:uppercase; color:rgba(255,255,255,0.65);">
                    Ideate &nbsp;·&nbsp; Curate &nbsp;·&nbsp; Execute
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Eyebrow + Title -->
          <tr>
            <td style="padding:36px 40px 8px 40px;">
              <table role="presentation" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="font-family:Arial,Helvetica,sans-serif; font-size:11px; font-weight:700; letter-spacing:2px; text-transform:uppercase; color:${COLORS.orange};">
                    New Website Enquiry
                  </td>
                </tr>
              </table>
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="padding-top:6px; font-family:Arial,Helvetica,sans-serif; font-size:22px; font-weight:800; color:${COLORS.navy};">
                    ${escapeHtml(name)} just reached out
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Details card -->
          <tr>
            <td style="padding:16px 40px 8px 40px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:${COLORS.ivory}; border-radius:12px; padding:20px 24px;">
                ${row('Name', name)}
                ${row('Email', email)}
                ${row('Phone', phone)}
                ${row('Company', company)}
                ${row('Event Type', eventType)}
              </table>
            </td>
          </tr>

          <!-- Message -->
          ${message ? `
          <tr>
            <td style="padding:20px 40px 8px 40px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="font-family:Arial,Helvetica,sans-serif; font-size:11px; font-weight:700; letter-spacing:1.5px; text-transform:uppercase; color:${COLORS.orange}; padding-bottom:8px;">
                    Message
                  </td>
                </tr>
                <tr>
                  <td style="font-family:Arial,Helvetica,sans-serif; font-size:15px; line-height:1.6; color:${COLORS.gray700}; background-color:${COLORS.white}; border:1px solid ${COLORS.gray300}; border-radius:12px; padding:18px 20px;">
                    ${escapeHtml(message).replace(/\n/g, '<br/>')}
                  </td>
                </tr>
              </table>
            </td>
          </tr>` : ''}

          <!-- CTA -->
          <tr>
            <td style="padding:28px 40px 40px 40px;" align="left">
              <table role="presentation" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="border-radius:999px; background-color:${COLORS.orange};">
                    <a href="mailto:${encodeURIComponent(email)}" style="display:inline-block; padding:14px 28px; font-family:Arial,Helvetica,sans-serif; font-size:14px; font-weight:700; letter-spacing:0.5px; color:${COLORS.white}; text-decoration:none;">
                      Reply to ${escapeHtml(name)}
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="background-color:${COLORS.navy}; padding:20px 40px; text-align:center;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="font-family:Arial,Helvetica,sans-serif; font-size:11px; color:rgba(255,255,255,0.55); text-align:center;">
                    This enquiry was submitted via the contact form on collectivekatalyx.com
                  </td>
                </tr>
              </table>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

export { buildEnquiryEmailHtml };
